import type { SizeUnits } from '../models/viewer-types';
import { formatFileSize } from '../utils/file-size';

// Reads a ZIP over HTTP range requests: stored and Deflate entries, single-volume ZIP64 catalogs.
// Format reference: https://pkware.cachefly.net/webdocs/casestudies/APPNOTE.TXT

/** One entry as the central directory describes it. */
export interface ZipEntry {
  id: number;
  name: string;
  /** The name exactly as stored, kept to check the local header against the catalog. */
  rawName: Uint8Array;
  flags: number;
  method: number;
  size: number;
  compressed: number;
  offset: number;
  directory: boolean;
  /** Why this entry cannot be opened; empty when it can. */
  reason: string;
  crc: number;
}

export interface RemoteZipOptions {
  signal: AbortSignal;
  sizeUnits?: SizeUnits;
  timeoutMs?: number;
  maxArchiveBytes?: number;
  maxFileBytes?: number;
  maxPdfBytes?: number;
  /** How the host application routes a name, when it decides that for itself. */
  formatForName?: (name: string) => string;
  onProgress?: (loaded: number, total?: number) => void;
}

export interface RemoteZip {
  entries: Array<ZipEntry>;
  size: number;
  /** Whether the server served ranges; false means the whole archive had to be downloaded. */
  range: boolean;
  readRange(entry: ZipEntry, offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array>;
  extract(entry: ZipEntry, signal?: AbortSignal): Promise<ArrayBuffer>;
}

/** What the limits say about one entry, before it becomes a {@link ZipEntry}. */
interface EntryCheck {
  name: string;
  flags: number;
  method: number;
  size: number;
  compressed: number;
  symlink: boolean;
  limit: number;
}

/** Where the central directory is, and how much of it there is. */
interface CatalogLocation {
  count: number;
  size: number;
  offset: number;
}

/** One answer from the server: the bytes it gave, and where in the archive they start. */
interface FetchedRange {
  bytes: Uint8Array;
  start: number;
}

const TAIL_BYTES = 65557;
const EOCD_SIZE = 22;
const LOCAL_HEADER_SIZE = 30;
const CATALOG_ENTRY_SIZE = 46;
const MAX_CATALOG_ENTRIES = 10000;
const MAX_CATALOG_BYTES = 8 * 1024 * 1024;
const MAX_COMPRESSED_BYTES = 100 * 1024 * 1024;
const FULL_DOWNLOAD_LIMIT = 100 * 1024 * 1024;
const DEFAULT_ARCHIVE_BYTES = 1024 * 1024 * 1024;
const DEFAULT_TIMEOUT_MS = 60000;
const MAX_RATIO = 1000;
const MAX_PATH_DEPTH = 64;
const MAX_NAME_BYTES = 4096;

const END_OF_CATALOG = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_END_OF_CATALOG = 0x06064b50;
const CATALOG_ENTRY = 0x02014b50;
const LOCAL_HEADER = 0x04034b50;

const ARCHIVE_CHANGED = 'The ZIP changed. Reopen the archive.';
const SPLIT_ARCHIVE = 'Split ZIP archives are not supported.';

const cp437 =
  'ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ';

const crcTable = Uint32Array.from({ length: 256 }, (_, index) => {
  let value = index;

  for (let bit = 0; bit < 8; bit++) value = (value >>> 1) ^ (value & 1 ? 0xedb88320 : 0);

  return value >>> 0;
});

function fail(message: string): never {
  throw Error(message);
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function safe64(data: DataView, offset: number): number {
  const number = Number(data.getBigUint64(offset, true));

  if (!Number.isSafeInteger(number)) fail('ZIP64 value exceeds browser integer precision.');

  return number;
}

function crcUpdate(start: number, bytes: Uint8Array): number {
  let crc = start;

  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 255] ^ (crc >>> 8);

  return crc;
}

function crc32(bytes: Uint8Array): number {
  return (crcUpdate(-1, bytes) ^ -1) >>> 0;
}

function utf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

function fields(bytes: Uint8Array): Map<number, Uint8Array> {
  const data = view(bytes);
  const result = new Map<number, Uint8Array>();

  for (let offset = 0; offset < bytes.length; ) {
    if (offset + 4 > bytes.length) fail('Invalid ZIP extra fields.');

    const type = data.getUint16(offset, true);
    const size = data.getUint16(offset + 2, true);

    offset += 4;

    if (offset + size > bytes.length) fail('Truncated ZIP extra field.');

    result.set(type, bytes.subarray(offset, offset + size));
    offset += size;
  }

  return result;
}

// The Unicode path extra field wins, but only where its checksum says it describes this very name.
function nameOf(bytes: Uint8Array, flags: number, extras: Map<number, Uint8Array>): string {
  const unicode = extras.get(0x7075);

  if ((unicode?.length ?? 0) >= 5 && unicode?.[0] === 1 && view(unicode).getUint32(1, true) === crc32(bytes)) {
    return utf8(unicode.subarray(5));
  }

  return flags & 0x800 ? utf8(bytes) : Array.from(bytes, (byte) => (byte < 128 ? String.fromCharCode(byte) : cp437[byte - 128])).join('');
}

// Anything that would make the entry name reach outside the folder it is extracted into.
function isUnsafePath(name: string): boolean {
  return (
    !name ||
    name.startsWith('/') ||
    /^[a-z]:/i.test(name) ||
    name.split('/').some((part) => part === '..' || part === '.') ||
    // eslint-disable-next-line no-control-regex
    /[\x00-\x1f\x7f]/.test(name) ||
    name.split('/').length > MAX_PATH_DEPTH
  );
}

/**
 * Why an entry cannot be opened.
 *
 * @param {EntryCheck} entry What the catalog says about it, and the size it is allowed to reach.
 * @return {string} The reason to show, or an empty string when the entry is fine.
 */
function reasonFor(entry: EntryCheck): string {
  if (isUnsafePath(entry.name)) return 'Unsafe archive path';

  if (entry.symlink) return 'Symbolic links are not supported';

  if (entry.flags & 0x2041) return 'Encrypted entries are not supported';

  if (![0, 8].includes(entry.method)) return 'Unsupported ZIP compression method';

  if (entry.size > entry.limit || entry.compressed > MAX_COMPRESSED_BYTES) {
    return 'File exceeds the configured size limit';
  }

  // A file that swells far beyond what it was stored as is a decompression bomb, not a document.
  if (entry.size > Math.max(1, entry.compressed) * MAX_RATIO) return 'Compression ratio exceeds the allowed limit';

  return '';
}

/**
 * Finds the end-of-catalog record, which is last in the archive but has a comment of any length after
 * it, so it is searched for backwards and confirmed by that comment's length.
 *
 * @param {Uint8Array} tail The end of the archive.
 * @param {DataView} tailView The same bytes, to read numbers from.
 * @return {number} Offset of the record within the tail.
 */
function endOfCatalogIn(tail: Uint8Array, tailView: DataView): number {
  for (let offset = tail.length - EOCD_SIZE; offset >= 0; offset--) {
    if (tailView.getUint32(offset, true) === END_OF_CATALOG && offset + EOCD_SIZE + tailView.getUint16(offset + 20, true) === tail.length) {
      return offset;
    }
  }

  fail('The URL does not contain a supported ZIP archive.');
}

/**
 * The entries of the central directory.
 *
 * @param {Uint8Array} bytes The catalog as it was read.
 * @param {CatalogLocation} catalog Where it sits and how many entries it holds.
 * @param {RemoteZipOptions} options The limits each entry is measured against.
 * @return {Array<ZipEntry>} One entry per file, in catalog order.
 */
function parseCatalog(bytes: Uint8Array, catalog: CatalogLocation, options: RemoteZipOptions): Array<ZipEntry> {
  const data = view(bytes);
  const entries: Array<ZipEntry> = [];

  for (let at = 0; entries.length < catalog.count; ) {
    if (at + CATALOG_ENTRY_SIZE > bytes.length || data.getUint32(at, true) !== CATALOG_ENTRY) {
      fail('Invalid ZIP catalog entry.');
    }

    const flags = data.getUint16(at + 8, true);
    const method = data.getUint16(at + 10, true);
    const nameSize = data.getUint16(at + 28, true);
    const extraSize = data.getUint16(at + 30, true);
    const commentSize = data.getUint16(at + 32, true);
    const next = at + CATALOG_ENTRY_SIZE + nameSize + extraSize + commentSize;

    if (next > bytes.length || nameSize > MAX_NAME_BYTES) fail('Invalid ZIP filename or entry length.');

    const from = at + CATALOG_ENTRY_SIZE;
    const rawName = bytes.slice(from, from + nameSize);
    const extras = fields(bytes.subarray(from + nameSize, from + nameSize + extraSize));

    let size = data.getUint32(at + 24, true);
    let compressed = data.getUint32(at + 20, true);
    let offset = data.getUint32(at + 42, true);
    let disk = data.getUint16(at + 34, true);

    // Values too large for their fields are parked at all-ones and given in full in the ZIP64 extra
    // field, which lists only those, in this order.
    if ([size, compressed, offset].includes(0xffffffff) || disk === 0xffff) {
      const zip64 = extras.get(1);

      if (!zip64) fail('ZIP64 entry is missing size metadata.');

      const data64 = view(zip64);
      let position = 0;
      const number = (): number => {
        if (position + 8 > data64.byteLength) fail('Truncated ZIP64 entry.');

        const value = safe64(data64, position);

        position += 8;

        return value;
      };

      if (size === 0xffffffff) size = number();

      if (compressed === 0xffffffff) compressed = number();

      if (offset === 0xffffffff) offset = number();

      if (disk === 0xffff) {
        if (position + 4 > data64.byteLength) fail('Truncated ZIP64 disk field.');

        disk = data64.getUint32(position, true);
      }
    }

    if (disk) fail('Split ZIP entries are not supported.');

    const name = nameOf(rawName, flags, extras).replaceAll('\\', '/');
    const isPdf = options.formatForName ? options.formatForName(name) === 'pdf' : /\.pdf$/i.test(name);
    const limit = (isPdf ? options.maxPdfBytes : options.maxFileBytes) ?? Infinity;
    const symlink = ((data.getUint32(at + 38, true) >>> 16) & 0xf000) === 0xa000;

    let reason = reasonFor({ name, flags, method, size, compressed, symlink, limit });

    if (offset + LOCAL_HEADER_SIZE + compressed > catalog.offset) reason ||= 'Invalid ZIP entry bounds';

    entries.push({
      id: entries.length,
      name,
      rawName,
      flags,
      method,
      size,
      compressed,
      offset,
      directory: name.endsWith('/'),
      reason,
      crc: data.getUint32(at + 16, true)
    });
    at = next;
  }

  return entries;
}

function inflate(bytes: Uint8Array): ReadableStream<Uint8Array> {
  try {
    return new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  } catch {
    fail('This browser does not support ZIP Deflate decompression.');
  }
}

/**
 * Opens a ZIP over HTTP without downloading it: the catalog comes from the end of the archive, and
 * each file is fetched by its own byte range when it is asked for. A server that does not serve
 * ranges is still read, by downloading the whole archive, within a much smaller limit.
 *
 * @param {string} url Link to the archive.
 * @param {RemoteZipOptions} options Limits, timeout, progress, and the way out.
 * @return {Promise<RemoteZip>} What the archive holds, and how to read it.
 */
export async function openRemoteZip(url: string, options: RemoteZipOptions): Promise<RemoteZip> {
  const maxArchiveBytes = options.maxArchiveBytes ?? DEFAULT_ARCHIVE_BYTES;
  const fullLimit = Math.min(maxArchiveBytes, FULL_DOWNLOAD_LIMIT);
  let total: number | undefined;
  let full: Uint8Array | undefined;
  let etag: string | null = null;
  let received = 0;

  const checkAbort = (signal?: AbortSignal): void => {
    if (signal?.aborted || options.signal.aborted) throw new DOMException('Cancelled', 'AbortError');
  };

  async function fetchBytes(range: string, expectedStart?: number, expectedLength?: number, signal?: AbortSignal): Promise<FetchedRange> {
    checkAbort(signal);

    const controller = new AbortController();
    const abort = (): void => controller.abort();

    options.signal.addEventListener('abort', abort, { once: true });
    signal?.addEventListener('abort', abort, { once: true });

    const timer = setTimeout(abort, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        headers: { Range: range },
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });

      if (!response.ok) {
        fail(`ZIP request returned HTTP ${response.status}. Check permissions, CORS, and signed URL expiry.`);
      }

      const partial = response.status === 206;
      const match = /^bytes (\d+)-(\d+)\/(\d+)$/i.exec(response.headers.get('content-range') ?? '');

      if (partial && !match) {
        await response.body?.cancel();
        fail('ZIP range access requires an exposed Content-Range header in CORS settings.');
      }

      const served = partial ? (match as RegExpExecArray) : null;
      const size = served ? Number(served[3]) : Number(response.headers.get('content-length')) || undefined;

      if (size != null && (!Number.isSafeInteger(size) || size > maxArchiveBytes)) {
        await response.body?.cancel();
        fail('The ZIP exceeds the configured archive size limit.');
      }

      if (total != null && size !== total) {
        await response.body?.cancel();
        fail(ARCHIVE_CHANGED);
      }

      const newEtag = response.headers.get('etag');

      if (etag && newEtag && etag !== newEtag) {
        await response.body?.cancel();
        fail(ARCHIVE_CHANGED);
      }

      if (newEtag) etag = newEtag;

      if (!partial && size != null && size > fullLimit) {
        await response.body?.cancel();
        fail(
          'This server does not support ZIP ranges. Full-download fallback is limited to ' +
            `${formatFileSize(FULL_DOWNLOAD_LIMIT, options.sizeUnits)}.`
        );
      }

      const start = served ? Number(served[1]) : 0;
      const advertised = served ? Number(served[2]) - start + 1 : size;
      const unexpected = served
        ? expectedStart != null
          ? start !== expectedStart || advertised !== expectedLength
          : start !== Math.max(0, (size as number) - TAIL_BYTES) || Number(served[2]) !== (size as number) - 1
        : false;

      if (unexpected) {
        await response.body?.cancel();
        fail('The server returned an unexpected ZIP byte range.');
      }

      const cap = partial ? (expectedLength ?? TAIL_BYTES) : fullLimit;
      const reader = (response.body as ReadableStream<Uint8Array>).getReader();
      const chunks: Array<Uint8Array> = [];
      let length = 0;

      try {
        for (;;) {
          const { done, value } = await reader.read();

          if (done) break;

          length += value.length;

          if (length > cap || (advertised != null && length > advertised)) fail('ZIP response exceeds the allowed size.');

          chunks.push(value);
          received += value.length;
          options.onProgress?.(received, size);
        }
      } catch (error) {
        await reader.cancel().catch(() => undefined);

        throw error;
      }

      if (advertised != null && length !== advertised) fail('The ZIP response was truncated.');

      const bytes = new Uint8Array(length);
      let offset = 0;

      for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.length;
      }

      total = size ?? length;

      if (!partial) full = bytes;

      return { bytes, start };
    } catch (error) {
      if (controller.signal.aborted && !options.signal.aborted && !signal?.aborted) {
        throw Error('The ZIP request timed out. Please try again.');
      }

      if ((error as Error).name === 'TypeError') {
        throw Error('Unable to read the ZIP. Check the URL, permissions, and CORS settings.');
      }

      throw error;
    } finally {
      clearTimeout(timer);
      options.signal.removeEventListener('abort', abort);
      signal?.removeEventListener('abort', abort);
    }
  }

  async function read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    checkAbort(signal);

    if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0 || offset + length > (total as number)) {
      fail('ZIP entry points outside the archive.');
    }

    if (!length) return new Uint8Array();

    if (full) return full.subarray(offset, offset + length);

    const result = await fetchBytes(`bytes=${offset}-${offset + length - 1}`, offset, length, signal);

    return full ? (full as Uint8Array).subarray(offset, offset + length) : result.bytes;
  }

  // ZIP64 keeps its own end-of-catalog record, found through a locator placed just before the usual one.
  async function catalogLocation(tailView: DataView, tailStart: number, end: number): Promise<CatalogLocation> {
    const count = tailView.getUint16(end + 10, true);
    const found: CatalogLocation = {
      count,
      size: tailView.getUint32(end + 12, true),
      offset: tailView.getUint32(end + 16, true)
    };

    if (tailView.getUint16(end + 8, true) !== count) fail(SPLIT_ARCHIVE);

    if (count !== 0xffff && found.size !== 0xffffffff && found.offset !== 0xffffffff) return found;

    const locator = view(await read(tailStart + end - 20, 20));

    if (locator.getUint32(0, true) !== ZIP64_LOCATOR || locator.getUint32(4, true) || locator.getUint32(16, true) !== 1) {
      fail('Invalid or split ZIP64 archive.');
    }

    const record = view(await read(safe64(locator, 8), 56));

    if (
      record.getUint32(0, true) !== ZIP64_END_OF_CATALOG ||
      safe64(record, 4) < 44 ||
      record.getUint32(16, true) ||
      record.getUint32(20, true)
    ) {
      fail('Invalid ZIP64 directory.');
    }

    const total64 = safe64(record, 32);

    if (safe64(record, 24) !== total64) fail('Split ZIP64 archives are not supported.');

    return { count: total64, size: safe64(record, 40), offset: safe64(record, 48) };
  }

  const first = await fetchBytes(`bytes=-${TAIL_BYTES}`);
  const tail = full ? full.subarray(Math.max(0, full.length - TAIL_BYTES)) : first.bytes;
  const tailStart = (total as number) - tail.length;
  const tailView = view(tail);
  const end = endOfCatalogIn(tail, tailView);

  if (tailView.getUint16(end + 4, true) || tailView.getUint16(end + 6, true)) fail(SPLIT_ARCHIVE);

  const catalog = await catalogLocation(tailView, tailStart, end);

  if (catalog.count > MAX_CATALOG_ENTRIES || catalog.size > MAX_CATALOG_BYTES) {
    fail(`ZIP catalog limit exceeded (10,000 entries / ${formatFileSize(MAX_CATALOG_BYTES, options.sizeUnits)}).`);
  }

  if (catalog.offset + catalog.size > tailStart + end) fail('Invalid ZIP directory bounds.');

  const entries = parseCatalog(await read(catalog.offset, catalog.size), catalog, options);
  const entryOffsets = new Map<number, number>();

  // The catalog is taken on trust only so far: the local header has to agree with it before its bytes
  // are read, or a crafted catalog could point the reader at another entry's data.
  async function entryStart(entry: ZipEntry, signal?: AbortSignal): Promise<number> {
    if (!entries.includes(entry) || entry.directory || entry.reason) {
      fail(entry.reason || 'Invalid ZIP file selection.');
    }

    const known = entryOffsets.get(entry.id);

    if (known != null) return known;

    const header = view(await read(entry.offset, LOCAL_HEADER_SIZE, signal));

    if (
      header.getUint32(0, true) !== LOCAL_HEADER ||
      header.getUint16(6, true) !== entry.flags ||
      header.getUint16(8, true) !== entry.method
    ) {
      fail('ZIP local header does not match its catalog.');
    }

    const nameLength = header.getUint16(26, true);
    const extraLength = header.getUint16(28, true);
    const name = await read(entry.offset + LOCAL_HEADER_SIZE, nameLength, signal);

    if (name.length !== entry.rawName.length || !name.every((byte, index) => byte === entry.rawName[index])) {
      fail('ZIP local filename does not match its catalog.');
    }

    const start = entry.offset + LOCAL_HEADER_SIZE + nameLength + extraLength;

    if (start + entry.compressed > catalog.offset) fail('ZIP file data overlaps its catalog.');

    entryOffsets.set(entry.id, start);

    return start;
  }

  return {
    entries,
    size: Number(total),
    range: !full,
    async readRange(entry: ZipEntry, offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
      const start = await entryStart(entry, signal);

      if (entry.method !== 0 || entry.compressed !== entry.size) {
        fail('This ZIP entry does not support direct byte ranges.');
      }

      if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0 || offset + length > entry.size) {
        fail('Document range exceeds its ZIP entry.');
      }

      return read(start + offset, length, signal);
    },
    async extract(entry: ZipEntry, signal?: AbortSignal): Promise<ArrayBuffer> {
      const start = await entryStart(entry, signal);
      const compressed = await read(start, entry.compressed, signal);
      const stream = entry.method === 0 ? new Blob([compressed as BlobPart]).stream() : inflate(compressed);
      const reader = stream.getReader();
      const chunks: Array<Uint8Array> = [];
      const cancel = (): void => {
        reader.cancel().catch(() => undefined);
      };

      options.signal.addEventListener('abort', cancel, { once: true });
      signal?.addEventListener('abort', cancel, { once: true });

      let length = 0;
      let crc = -1;

      try {
        for (;;) {
          checkAbort(signal);

          const { done, value } = await reader.read();

          if (done) break;

          length += value.length;

          if (length > entry.size) fail('ZIP file expands beyond its declared size.');

          crc = crcUpdate(crc, value);
          chunks.push(value);
        }

        checkAbort(signal);

        if (length !== entry.size || (crc ^ -1) >>> 0 !== entry.crc) {
          fail('ZIP file failed its size or CRC integrity check.');
        }

        const output = new Uint8Array(length);
        let offset = 0;

        for (const chunk of chunks) {
          output.set(chunk, offset);
          offset += chunk.length;
        }

        return output.buffer as ArrayBuffer;
      } finally {
        await reader.cancel().catch(() => undefined);
        options.signal.removeEventListener('abort', cancel);
        signal?.removeEventListener('abort', cancel);
      }
    }
  };
}
